import GppMaybeRounded from '@mui/icons-material/GppMaybeRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { BrandMark, ModeToggle } from '../../layout/parts';
import { MfaSetupFlow } from './MfaSetupFlow';

/**
 * The forced enrolment screen: while the server requires MFA (`mfaRequired && !mfaEnabled`) every
 * other route redirects here and the API answers 403 MFA_SETUP_REQUIRED.
 */
export function ForcedMfaSetupPage() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Box
      sx={(t) => ({
        minHeight: '100dvh',
        bgcolor: 'background.default',
        backgroundImage:
          'radial-gradient(900px 420px at 50% -10%, rgba(52,168,135,0.16), transparent 70%)',
        ...t.applyStyles('dark', {
          backgroundImage:
            'radial-gradient(900px 420px at 50% -10%, rgba(52,168,135,0.22), transparent 70%)',
        }),
      })}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', px: { xs: 2, sm: 4 }, height: 68 }}
      >
        <BrandMark size={34} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
            PackPilot
          </Typography>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 650 }}>
            Platform console
          </Typography>
        </Box>
        <ModeToggle />
        <Button
          variant="outlined"
          size="small"
          startIcon={<LogoutRounded />}
          onClick={() => void logout().then(() => navigate('/login'))}
        >
          Sign out
        </Button>
      </Stack>
      <Box sx={{ maxWidth: 820, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 5 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3 }}
        >
          <Box
            sx={(t) => {
              const c = t.vars?.palette.warning.main ?? t.palette.warning.main;
              return {
                width: 52,
                height: 52,
                flexShrink: 0,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                color: 'warning.main',
                bgcolor: `color-mix(in srgb, ${c} 12%, transparent)`,
                border: `1px solid color-mix(in srgb, ${c} 22%, transparent)`,
              };
            }}
          >
            <GppMaybeRounded />
          </Box>
          <Box>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontSize: { xs: '1.375rem', sm: '1.5rem' } }}
            >
              Set up two-step verification
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              This platform requires it for every admin. It takes about a minute.
            </Typography>
          </Box>
        </Stack>
        <Alert severity="info" sx={{ mb: 2.5 }}>
          Signed in as <strong>{me?.email}</strong>. The console unlocks as soon as two-step
          verification is on.
        </Alert>
        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <MfaSetupFlow onDone={() => void navigate('/', { replace: true })} />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
